import React, { useRef, useState } from 'react';
import './SpreadsheetGrid.css';

type Cell = string;
export type SpreadsheetValue = Cell[][];

interface SpreadsheetGridProps {
  rows: number;
  cols: number;
  value: SpreadsheetValue;
  onChange: (next: SpreadsheetValue) => void;
  /** Labels customizados para os headers das colunas (substituem A, B, C...). */
  colHeaders?: string[];
  /** Labels customizados para os headers das linhas (substituem 1, 2, 3...). */
  rowHeaders?: string[];
  /** Largura da coluna de headers de linha. */
  rowHeaderWidth?: number;
  /** Esconde a primeira linha (headers de colunas). */
  hideColHeaders?: boolean;
  /** Esconde a primeira coluna (headers de linhas). */
  hideRowHeaders?: boolean;
  /** Marca células como "label" — visual cinza/bold, mas continuam editáveis e
   *  recebem paste normalmente (permite colar uma planilha completa a partir
   *  da primeira célula). */
  lockedCells?: boolean[][];
  /** Quando true, todas as células ficam read-only (não permite edição). */
  readOnly?: boolean;
}

const COL_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

export function makeEmptyGrid(rows: number, cols: number): SpreadsheetValue {
  return Array.from({ length: rows }, () => Array.from({ length: cols }, () => ''));
}

export function SpreadsheetGrid({
  rows,
  cols,
  value,
  onChange,
  colHeaders,
  rowHeaders,
  rowHeaderWidth = 40,
  hideColHeaders = false,
  hideRowHeaders = false,
  lockedCells,
  readOnly = false,
}: SpreadsheetGridProps) {
  const [focus, setFocus] = useState<{ r: number; c: number } | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  function isLabel(r: number, c: number): boolean {
    return Boolean(lockedCells?.[r]?.[c]);
  }

  function setCell(r: number, c: number, v: string) {
    const next = value.map((row) => row.slice());
    if (!next[r]) next[r] = Array.from({ length: cols }, () => '');
    next[r][c] = v;
    onChange(next);
  }

  // Paste tabular content (TSV / CSV) starting at focused cell.
  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>, r: number, c: number) {
    const text = e.clipboardData.getData('text');
    if (!text.includes('\t') && !text.includes('\n')) return;
    e.preventDefault();
    const lines = text.replace(/\r/g, '').split('\n').filter((l) => l.length > 0);
    const next = value.map((row) => row.slice());
    lines.forEach((line, lineIdx) => {
      const cells = line.includes('\t') ? line.split('\t') : line.split(',');
      cells.forEach((val, colIdx) => {
        const targetR = r + lineIdx;
        const targetC = c + colIdx;
        if (targetR < rows && targetC < cols) {
          if (!next[targetR]) next[targetR] = Array.from({ length: cols }, () => '');
          next[targetR][targetC] = val.trim();
        }
      });
    });
    onChange(next);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>, r: number, c: number) {
    const moveFocus = (nr: number, nc: number) => {
      if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) return;
      const next = gridRef.current?.querySelector<HTMLInputElement>(
        `input[data-cell="${nr}-${nc}"]`,
      );
      next?.focus();
      next?.select();
    };
    if (e.key === 'ArrowDown' || (e.key === 'Enter' && !e.shiftKey)) {
      e.preventDefault();
      moveFocus(r + 1, c);
    } else if (e.key === 'ArrowUp' || (e.key === 'Enter' && e.shiftKey)) {
      e.preventDefault();
      moveFocus(r - 1, c);
    } else if (e.key === 'ArrowLeft' && (e.currentTarget.selectionStart ?? 0) === 0) {
      e.preventDefault();
      moveFocus(r, c - 1);
    } else if (
      e.key === 'ArrowRight' &&
      (e.currentTarget.selectionStart ?? 0) === (e.currentTarget.value.length ?? 0)
    ) {
      e.preventDefault();
      moveFocus(r, c + 1);
    } else if (e.key === 'Tab') {
      e.preventDefault();
      moveFocus(r, c + (e.shiftKey ? -1 : 1));
    }
  }

  const colHeaderLabels = Array.from({ length: cols }).map(
    (_, c) => colHeaders?.[c] ?? COL_LETTERS[c] ?? String(c + 1),
  );
  const rowHeaderLabels = Array.from({ length: rows }).map(
    (_, r) => rowHeaders?.[r] ?? String(r + 1),
  );

  const gridTemplateColumns = hideRowHeaders
    ? `repeat(${cols}, 1fr)`
    : `${rowHeaderWidth}px repeat(${cols}, 1fr)`;

  return (
    <div className="ssh-grid-wrapper">
      <div
        className="ssh-grid"
        ref={gridRef}
        style={{ gridTemplateColumns }}
      >
        {!hideColHeaders && (
          <>
            {!hideRowHeaders && <div className="ssh-corner" />}
            {colHeaderLabels.map((label, c) => (
              <div key={`h-${c}`} className="ssh-col-header">
                {label}
              </div>
            ))}
          </>
        )}

        {Array.from({ length: rows }).map((_, r) => (
          <React.Fragment key={`r-${r}`}>
            {!hideRowHeaders && (
              <div
                className={`ssh-row-header${rowHeaders ? ' is-named' : ''}`}
                title={rowHeaderLabels[r]}
              >
                {rowHeaderLabels[r]}
              </div>
            )}
            {Array.from({ length: cols }).map((_, c) => {
              const isFocused = focus?.r === r && focus?.c === c;
              const labelStyle = isLabel(r, c);
              return (
                <input
                  key={`c-${r}-${c}`}
                  data-cell={`${r}-${c}`}
                  className={`ssh-cell${isFocused ? ' is-focused' : ''}${labelStyle ? ' is-locked' : ''}${readOnly ? ' is-readonly' : ''}`}
                  value={value[r]?.[c] ?? ''}
                  readOnly={readOnly}
                  tabIndex={readOnly ? -1 : 0}
                  onChange={(e) => !readOnly && setCell(r, c, e.target.value)}
                  onFocus={() => !readOnly && setFocus({ r, c })}
                  onBlur={() => setFocus(null)}
                  onKeyDown={(e) => !readOnly && handleKeyDown(e, r, c)}
                  onPaste={(e) => !readOnly && handlePaste(e, r, c)}
                />
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
